import { Injectable, Inject, Optional, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { DOCUMENT } from '@angular/common';
import { DateAdapter } from '@angular/material/core';

export type LangItem = { code: string; label: string };

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private readonly supported: LangItem[] = [
    { code: 'pt', label: 'PT' },
    { code: 'fr', label: 'FR' },
    { code: 'en', label: 'EN' },
    { code: 'es', label: 'ES' },
    { code: 'it', label: 'IT' },
  ];

  current$ = new BehaviorSubject<string>('pt');
  get current(): string {
    return this.current$.value;
  }

  constructor(
    private t: TranslateService,
    @Inject(DOCUMENT) private doc: Document,
    @Optional() private dateAdapter: DateAdapter<Date>,
    @Inject(PLATFORM_ID) private platformId: Object,
  ) {
    this.t.addLangs(this.supported.map(l => l.code));
    this.t.setDefaultLang('pt');
    (this.t as any).useDefaultLang = true;
  }

  /** chamado no APP_INITIALIZER */
  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      this.use('pt');
      return;
    }

    let urlLang = null;
    try {
      if (this.doc && this.doc.defaultView && this.doc.defaultView.location) {
        urlLang = new URL(this.doc.defaultView.location.href, this.doc.baseURI || 'http://localhost').searchParams.get(
          'lang',
        );
      }
    } catch (e) {
      // Ignorar erros de parsing do URL
    }

    const saved = urlLang || this.safeGet('lang') || this.guess();
    this.use(saved);
  }

  private safeGet(key: string): string | null {
    if (!isPlatformBrowser(this.platformId)) return null;
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private safeSet(key: string, val: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    try {
      localStorage.setItem(key, val);
    } catch {}
  }

  list(): LangItem[] {
    return this.supported;
  }

  use(code: string): void {
    const lang = this.normalize(code);
    this.t.use(lang).subscribe({
      next: () => {
        this.safeSet('lang', lang);
        if (this.doc && this.doc.documentElement) {
          this.doc.documentElement.lang = lang;
        }
        if (this.dateAdapter) {
          this.dateAdapter.setLocale(lang);
        }
        this.current$.next(lang);
      },
    });
  }

  private normalize(code: string | null | undefined): string {
    const short = (code || 'pt').toLowerCase().split('-')[0];
    return this.supported.some(l => l.code === short) ? short : 'pt';
  }

  private guess(): string {
    if (!isPlatformBrowser(this.platformId)) return 'pt';

    try {
      const userAgent = window.navigator.userAgent.toLowerCase();
      const isBot = /bot|googlebot|crawler|spider|robot|crawling/i.test(userAgent);

      if (isBot) {
        return 'pt';
      }

      const nav = (navigator.language || 'pt').toLowerCase();
      return this.normalize(nav);
    } catch (e) {
      return 'pt';
    }
  }
}
